import React from "react";
import { Link } from "react-router-dom";
import { PsyGestionIcon } from "@/components/PsyGestionLogo";

const C = {
  navy:   "#1B3A5C",
  teal:   "#2A9D8F",
  border: "#D6E4F0",
  muted:  "#6B85A3",
  bg:     "#F7FAFE",
  white:  "#FFFFFF",
};

const sections: { title: string; content: React.ReactNode }[] = [
  {
    title: "1. Responsable du traitement",
    content: (
      <p>
        Freedom Architect —{" "}
        <a href="mailto:contact@freedomarchitect.io" style={{ color: C.teal }}>
          contact@freedomarchitect.io
        </a>
      </p>
    ),
  },
  {
    title: "2. Données collectées",
    content: (
      <ul style={{ paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 }}>
        <li>Adresse email (identification et communication)</li>
        <li>
          Données financières professionnelles saisies volontairement : chiffre d'affaires,
          recettes, charges estimées
        </li>
        <li>Date et heure de connexion (logs techniques)</li>
      </ul>
    ),
  },
  {
    title: "3. Finalité et base légale",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <p>
          <strong>Finalité :</strong> fourniture du service PsyGestion.
        </p>
        <p>
          <strong>Base légale :</strong> exécution du contrat (article 6.1.b RGPD).
        </p>
      </div>
    ),
  },
  {
    title: "4. Hébergement et sécurité",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <p>
          Les données sont hébergées chez <strong>Supabase</strong>, infrastructure localisée
          dans l'Union Européenne.
        </p>
        <p>Les connexions sont chiffrées (HTTPS/TLS).</p>
        <p>
          Les données sont isolées par utilisatrice via{" "}
          <strong>Row Level Security (RLS)</strong>.
        </p>
      </div>
    ),
  },
  {
    title: "5. Durée de conservation",
    content: (
      <p>
        Données conservées pendant la durée de l'abonnement actif, puis{" "}
        <strong>30 jours</strong> après résiliation ou expiration de l'essai, puis supprimées
        définitivement.
      </p>
    ),
  },
  {
    title: "6. Vos droits RGPD",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <p>Vous disposez des droits suivants :</p>
        <ul style={{ paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 }}>
          <li>
            <strong>Accès</strong> à vos données (export CSV disponible dans les paramètres
            du compte)
          </li>
          <li>
            <strong>Rectification</strong>
          </li>
          <li>
            <strong>Suppression</strong> (bouton « Supprimer mon compte » dans les paramètres)
          </li>
          <li>
            <strong>Portabilité</strong>
          </li>
        </ul>
        <p>
          Pour exercer ces droits :{" "}
          <a href="mailto:contact@freedomarchitect.io" style={{ color: C.teal }}>
            contact@freedomarchitect.io
          </a>
          . Réponse sous 30 jours.
        </p>
      </div>
    ),
  },
  {
    title: "7. Partage avec des tiers",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <p>
          Vos données ne sont jamais vendues ni partagées avec des tiers à des fins
          commerciales.
        </p>
        <p>
          Partage limité aux sous-traitants techniques nécessaires au fonctionnement du
          service :
        </p>
        <ul style={{ paddingLeft: 20, display: "flex", flexDirection: "column", gap: 4 }}>
          <li>
            <strong>Supabase</strong> — hébergement des données
          </li>
          <li>
            <strong>Stripe</strong> — traitement des paiements
          </li>
          <li>
            <strong>Resend</strong> — emails transactionnels
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "8. Cookies",
    content: (
      <p>
        PsyGestion utilise uniquement des cookies d'authentification nécessaires au
        fonctionnement du service.{" "}
        <strong>Aucun cookie de tracking ou publicitaire.</strong>
      </p>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: "100vh", backgroundColor: C.bg, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ backgroundColor: C.white, borderBottom: `1px solid ${C.border}`, padding: "16px 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 16 }}>
          <Link
            to="/"
            style={{ color: C.muted, textDecoration: "none", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
          >
            ← Accueil
          </Link>
          <span style={{ color: C.border }}>·</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.navy, display: "flex", alignItems: "center", gap: 4 }}><PsyGestionIcon size={16} /> PsyGestion</span>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px 80px" }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: C.navy, marginBottom: 8 }}>
          Politique de confidentialité
        </h1>
        <p style={{ fontSize: 13, color: C.muted, marginBottom: 48 }}>
          Dernière mise à jour : mars 2026 — Conforme au RGPD
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
          {sections.map((s) => (
            <section key={s.title}>
              <h2
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: C.navy,
                  marginBottom: 12,
                  paddingBottom: 8,
                  borderBottom: `2px solid ${C.teal}30`,
                }}
              >
                {s.title}
              </h2>
              <div style={{ fontSize: 15, lineHeight: 1.75, color: "#374151" }}>
                {s.content}
              </div>
            </section>
          ))}
        </div>

        {/* Footer links */}
        <div
          style={{
            marginTop: 64,
            paddingTop: 24,
            borderTop: `1px solid ${C.border}`,
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            fontSize: 13,
            color: C.muted,
          }}
        >
          <Link to="/cgu" style={{ color: C.teal, textDecoration: "none" }}>
            Conditions Générales d'Utilisation
          </Link>
          <span>·</span>
          <a href="mailto:contact@freedomarchitect.io" style={{ color: C.teal, textDecoration: "none" }}>
            contact@freedomarchitect.io
          </a>
        </div>
      </div>
    </div>
  );
}
